export type MonthAbbrev =
  | 'Jan' | 'Feb' | 'Mar' | 'Apr' | 'May' | 'Jun'
  | 'Jul' | 'Aug' | 'Sep' | 'Oct' | 'Nov' | 'Dec';

export const MONTH_ORDER: readonly MonthAbbrev[] = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

export const MONTH_FULL_NAMES: Record<MonthAbbrev, string> = {
  Jan: 'January', Feb: 'February', Mar: 'March', Apr: 'April',
  May: 'May', Jun: 'June', Jul: 'July', Aug: 'August',
  Sep: 'September', Oct: 'October', Nov: 'November', Dec: 'December',
};

/** Raw shape returned by the eviction numbers API, keyed by year -> month. */
export interface EvictionMonthStats {
  total_evictions_individuals: number;
  total_evictions_households: number;
}

export type EvictionsByYearPerMonth = Record<
  string,
  Partial<Record<MonthAbbrev, EvictionMonthStats>>
>;

/**
 * Filter state driven by the report-generation modal.
 * `month` is required whenever `mode` is 'month' or 'both' — a bare month
 * with no year doesn't mean anything, so we always carry a year and let
 * `mode` decide whether MTD content renders.
 */
export interface ReportFilters {
  mode: 'year' | 'month' | 'both';
  year: number;
  month?: MonthAbbrev;
}

/** Returns the default filter state: current year, current month, mode 'both'. */
export function getDefaultFilters(now: Date = new Date()): ReportFilters {
  return {
    mode: 'both',
    year: now.getFullYear(),
    month: MONTH_ORDER[now.getMonth()],
  };
}

/*** Risk Map Stats***/
export interface DistrictRiskStats {
  total_individuals_at_risk: number;
  total_households_at_risk: number;
  /** Named locations within the district driving the risk count. */
  catchment_areas_at_risk?: string[];
}

/** Raw shape of the areas-at-risk API — a flat snapshot, no year/month keys.
 * The endpoint was built as a single current-state view, so this section
 * always reflects "right now" regardless of the eviction-numbers filters. */
export type EvictionRiskMappingPerDistrict = Record<string, DistrictRiskStats>;

/** Same shape as EvictionsByYearPerMonth (confirmed: identical field names)
 * — the "flip side" of evictions, tracking preventive/response reach
 * instead of displacement. */
export type PreventionsByYearPerMonth = EvictionsByYearPerMonth;


/** A single district's stats at one point in time (one snapshot slice of RiskByYearMonthDistrict). */

/** Raw shape of the areas-at-risk API, now year/month aware to match the
 * eviction-numbers filters. Unlike eviction counts (a flow, summable for
 * YTD), risk data is a stock — "households at risk right now" — so a
 * selected month always means "read that month's snapshot directly,"
 * never "sum every month up to it." */
export type RiskByYearMonthDistrict = Record<
  string,
  Partial<Record<MonthAbbrev, EvictionRiskMappingPerDistrict>>
>;

export type RiskBand = 'extreme' | 'high' | 'medium' | 'low';

/** Raw shape of the infrastructure-destroyed API — same year/month shape
 * as evictions (a flow: new destruction each month, summable for YTD),
 * already pre-aggregated server-side. */
export interface InfrastructureMonthStats {
  schools: number;
  health_centres: number;
  distribution_centres: number;
  protection_counselling: number;
  solar_lights: number;
  water_points: number;
  latrines: number;
  nutrition_centres: number;
  psn_houses: number;
  other_humanitarian_facility_count: number;
}

export type InfrastructureByYearPerMonth = Record<
  string,
  Partial<Record<MonthAbbrev, InfrastructureMonthStats>>
>;